// EventDetailDialog.js
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { TimeSelect } from '../../ui/TimeSelect';
import { Badge } from '../../ui/badge';
import { toast } from 'sonner';
import { activityAPI, hospitalityAPI, marketingAPI } from '../../../services/api';
import { Pencil, AlertCircle, Calendar, User, Link2, Clock, Sparkles, Trash2, Mail, MessageSquare, CheckCircle2, Upload, Camera, DollarSign, FileText, Gift, Users, Star, Building2, Download, EyeOff } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { associationAPI } from '../../../services/api';
import api from '../../../services/api';
import { useConfirm } from '../../../components/common/ConfirmDialog';
import { Checkbox } from '../../ui/checkbox';

export const EventDetailDialog = ({ open, onOpenChange, event, usersMap = {}, entities = {}, onNavigate, onSuccess }) => {
  const { user: currentUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({});
  const confirm = useConfirm();
  
  if (!event) return null;

  const startEditing = () => {
    const startStr = event.start_date_time ? String(event.start_date_time) : '';
    const endStr = event.end_date_time ? String(event.end_date_time) : startStr;
    setEditForm({
      activity_name: event.activity_name || '',
      description: event.description || '',
      event_type: event.event_type || 'other',
      company_wide: event.company_wide || false,
      all_day: event.all_day ?? true,
      start_date_time: startStr.split('T')[0] || '',
      start_time: startStr.includes('T') ? startStr.split('T')[1]?.substring(0, 5) || '09:00' : '09:00',
      end_date_time: endStr.split('T')[0] || '',
      end_time: endStr.includes('T') ? endStr.split('T')[1]?.substring(0, 5) || '10:00' : '10:00',
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let startDateTime, endDateTime;
      if (editForm.all_day) {
        startDateTime = editForm.start_date_time + 'T12:00:00';
        endDateTime = (editForm.end_date_time || editForm.start_date_time) + 'T12:00:00';
      } else {
        startDateTime = editForm.start_date_time + 'T' + (editForm.start_time || '09:00');
        endDateTime = (editForm.end_date_time || editForm.start_date_time) + 'T' + (editForm.end_time || '10:00');
      }
      await activityAPI.update(event.id, {
        activity_name: editForm.activity_name,
        description: editForm.description,
        event_type: editForm.event_type,
        company_wide: editForm.company_wide,
        all_day: editForm.all_day,
        start_date_time: startDateTime,
        end_date_time: endDateTime,
      });
      toast.success('Event updated');
      setIsEditing(false);
      if (onSuccess) onSuccess();
      onOpenChange(false);
    } catch {
      toast.error('Failed to update event');
    } finally {
      setSaving(false);
    }
  };

  const getEventTypeColor = (eventType) => {
    const colors = {
      birthday: 'bg-blue-100 text-blue-800',
      race: 'bg-purple-100 text-purple-800',
      sale: 'bg-green-100 text-green-800',
      deadline: 'bg-red-100 text-red-800',
      reminder: 'bg-orange-100 text-orange-800',
      other: 'bg-white text-gray-800',
    };
    return colors[eventType] || colors.other;
  };
  
  const linkedEntity = event.crm_entity_id ? entities[event.crm_entity_id] : null;
  
  // System-generated: auto-created events have no created_by_id.
  // Birthday and sale events are always auto-created. Deadline and system_wide
  // events are only system-generated when there is no creator (auto-created by
  // create_sale_events), NOT when a user manually chose "Deadline" as the type.
  const isSystemGenerated = event.event_type === 'birthday'
    || event.event_type === 'sale'
    || (event.event_type === 'deadline' && !event.created_by_id)
    || (event.system_wide && !event.created_by_id);
  
  // Check if user can edit (only events they created, not system-generated)
  const canEdit = !isSystemGenerated && event && !event.linked_activity_id && event.created_by_id === currentUser?.id;

  const handleHide = async () => {
    if (!event?.id) return;
    if (!await confirm({ title: 'Are you sure you want to hide this event? This action cannot be reversed and the event will no longer appear on your calendar.', destructive: true })) return;
    
    setDeleting(true);
    try {
      await activityAPI.update(event.id, { hidden_by_users: [...(event.hidden_by_users || []), currentUser?.id] });
      toast.success('Event hidden');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      // Fallback: try direct delete for system events
      try {
        await api.put(`/activities/${event.id}/hide`);
        toast.success('Event hidden');
        onOpenChange(false);
        if (onSuccess) onSuccess();
      } catch {
        console.error('Failed to hide event:', error);
        toast.error('Failed to hide event');
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleDelete = async () => {
    if (!event?.id) return;
    if (!await confirm({ title: 'Are you sure you want to delete this event?', destructive: true })) return;
    
    setDeleting(true);
    try {
      await activityAPI.delete(event.id);
      toast.success('Event deleted');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Failed to delete event:', error);
      toast.error('Failed to delete event');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="event-detail-dialog">
        <DialogHeader>
          <DialogTitle>Event Details</DialogTitle>
        </DialogHeader>

        {isEditing ? (
          <div className="space-y-4 py-1">
            <div className="space-y-2">
              <Label>Event Name *</Label>
              <Input value={editForm.activity_name} onChange={e => setEditForm({...editForm, activity_name: e.target.value})} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Event Type</Label>
                <Select value={editForm.event_type} onValueChange={val => setEditForm({...editForm, event_type: val})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="birthday">Birthday</SelectItem>
                    <SelectItem value="race">Race</SelectItem>
                    <SelectItem value="sale">Sale</SelectItem>
                    <SelectItem value="deadline">Deadline</SelectItem>
                    <SelectItem value="reminder">Reminder</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-4 pt-6">
                <div className="flex items-center gap-2">
                  <Checkbox id="edit-company-wide" checked={!!editForm.company_wide} onCheckedChange={(v) => setEditForm({...editForm, company_wide: v})} />
                  <Label htmlFor="edit-company-wide" className="text-sm cursor-pointer">Company Wide</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox id="edit-all-day" checked={!!editForm.all_day} onCheckedChange={(v) => setEditForm({...editForm, all_day: v})} />
                  <Label htmlFor="edit-all-day" className="text-sm cursor-pointer">All Day</Label>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input type="date" value={editForm.start_date_time} onChange={e => setEditForm({...editForm, start_date_time: e.target.value})} />
              </div>
              {!editForm.all_day && (
                <div className="space-y-2">
                  <Label>Start Time</Label>
                  <TimeSelect value={editForm.start_time} onChange={v => setEditForm({...editForm, start_time: v})} />
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input type="date" value={editForm.end_date_time} onChange={e => setEditForm({...editForm, end_date_time: e.target.value})} />
              </div>
              {!editForm.all_day && (
                <div className="space-y-2">
                  <Label>End Time</Label>
                  <TimeSelect value={editForm.end_time} onChange={v => setEditForm({...editForm, end_time: v})} />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={editForm.description} onChange={e => setEditForm({...editForm, description: e.target.value})} rows={3} placeholder="Additional details..." />
            </div>
          </div>
        ) : (
          <div className="space-y-3 py-1">
          <div className="p-4 bg-muted/30 rounded">
            {event.is_sensitive && (
              <Badge variant="destructive" className="flex items-center gap-1 w-fit mb-2">
                <AlertCircle className="w-3 h-3" />
                Sensitive
              </Badge>
            )}
            
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-lg font-semibold">{event.activity_name}</h3>
              <Badge className={`capitalize ml-3 ${getEventTypeColor(event.event_type)}`}>
                {event.event_type?.replace('_', ' ') || 'Event'}
              </Badge>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Date:</span>
                <span>
                  {event.start_date_time ? (() => {
                    const d = String(event.start_date_time).split('T')[0];
                    const [y, m, day] = d.split('-');
                    return `${parseInt(m)}/${parseInt(day)}/${y}`;
                  })() : 'Not set'}
                  {event.all_day && <span className="ml-2 text-xs text-muted-foreground">(All Day)</span>}
                </span>
              </div>
              
              {!event.all_day && event.start_date_time && (
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Time:</span>
                  <span>
                    {new Date(event.start_date_time).toLocaleTimeString('en-US', { 
                      hour: 'numeric', 
                      minute: '2-digit' 
                    })}
                  </span>
                </div>
              )}
              
              {event.assigned_to_user_id && (
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Assigned:</span>
                  <span>{usersMap[event.assigned_to_user_id] || 'Unknown'}</span>
                </div>
              )}
              
              {event.linked_activity_id && linkedEntity && (
                <div className="flex items-center gap-2">
                  <Link2 className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">{linkedEntity.crm_class === 'account' ? 'Account' : 'Contact'}:</span>
                  <span 
                    className="text-primary font-medium hover:underline cursor-pointer"
                    onClick={() => {
                      if (onNavigate) {
                        onNavigate(`/crm/accounts/${event.crm_entity_id}`);
                        onOpenChange(false);
                      }
                    }}
                  >
                    {linkedEntity.system_name}{linkedEntity.additional_identifier ? ` (${linkedEntity.additional_identifier})` : ''}
                  </span>
                </div>
              )}
              
              {event.linked_activity_id && (
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Source:</span>
                  <span className="text-xs text-muted-foreground">Auto-generated from task</span>
                </div>
              )}
            </div>
          </div>

          {event.description && (
            <div className="space-y-1 pt-2">
              <Label className="text-sm font-medium">Notes</Label>
              {(() => {
                const parts = event.description.split('\n---\n');
                return (
                  <>
                    <p className="text-sm whitespace-pre-wrap text-muted-foreground">{parts[0]}</p>
                    {parts[1] && (
                      <p className="text-xs italic text-muted-foreground/70 whitespace-pre-wrap mt-1">
                        {parts[1]}
                      </p>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          <div className="text-xs text-muted-foreground pt-2 border-t">
            Created: {new Date(event.created_at).toLocaleDateString()} by {usersMap[event.created_by_id] || 'Unknown'}
          </div>
        </div>
        )}

        <DialogFooter>
          {isEditing ? (
            <div className="flex justify-between w-full">
              <Button variant="ghost" size="icon" onClick={handleDelete} disabled={deleting || saving} data-testid="delete-event-btn">
                <Trash2 className="w-4 h-4" />
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setIsEditing(false)} disabled={saving}>Cancel</Button>
                <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
              </div>
            </div>
          ) : isSystemGenerated ? (
            <div className="flex flex-col gap-2 w-full">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm">
                <p className="text-amber-900 font-medium">System-Generated Event</p>
                <p className="text-amber-700 text-xs mt-1">
                  This event was automatically created by the system. You can hide it, but this action cannot be reversed.
                </p>
              </div>
              <div className="flex justify-end">
                <Button variant="outline" onClick={handleHide} disabled={deleting}>
                  {deleting ? 'Hiding...' : 'Hide Event'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex justify-between w-full">
              {canEdit && (
                <Button variant="ghost" size="icon" onClick={startEditing} data-testid="edit-event-btn">
                  <Pencil className="w-4 h-4" />
                </Button>
              )}
              <Button variant="outline" onClick={() => onOpenChange(false)} className="ml-auto">
                Close
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

